import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
	adminRequestKeys,
	approveAdminRequest,
	declineAdminRequest,
	reopenAdminRequest,
} from "@/features/applications/api/admin-requests";
import { getMutationErrorMessage } from "@/shared/api/errors";

export function useRequestMutations(requestId: string) {
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	const invalidate = async () => {
		await Promise.all([
			queryClient.invalidateQueries({ queryKey: adminRequestKeys.all }),
			queryClient.invalidateQueries({ queryKey: adminRequestKeys.counts }),
			queryClient.invalidateQueries({
				queryKey: adminRequestKeys.detail(requestId),
			}),
		]);
	};

	const onError = async (error: unknown) => {
		const message = await getMutationErrorMessage(error);
		notifications.show({
			color: "red",
			title: t("verification.decisionFailed"),
			message:
				message === "REQUEST_ALREADY_REVIEWED"
					? t("verification.alreadyReviewed")
					: message,
		});
		await invalidate();
	};

	const approve = useMutation({
		mutationFn: (expiresAt?: string) =>
			approveAdminRequest(requestId, expiresAt ? { expiresAt } : {}),
		onSuccess: async () => {
			notifications.show({
				color: "green",
				title: t("verification.approve"),
				message: t("verification.approvedToast"),
			});
			await invalidate();
		},
		onError,
	});

	const decline = useMutation({
		mutationFn: (comment: string) =>
			declineAdminRequest(requestId, { comment }),
		onSuccess: async () => {
			notifications.show({
				color: "red",
				title: t("verification.decline"),
				message: t("verification.declinedToast"),
			});
			await invalidate();
		},
		onError,
	});

	const reopen = useMutation({
		mutationFn: (comment: string) => reopenAdminRequest(requestId, { comment }),
		onSuccess: async () => {
			notifications.show({
				color: "orange",
				title: t("verification.rework"),
				message: t("verification.reworkToast"),
			});
			await invalidate();
		},
		onError,
	});

	return { approve, decline, reopen };
}
